import { useEffect, useMemo, useState } from "react";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";
import { Link, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ymdLocal } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useSport, SPORT_META } from "@/context/SportContext";
import { Plus, Trash2, Sparkles, Users, CalendarDays, X } from "lucide-react";
import { toast } from "sonner";

// Iter134 — Shared sub-tabs so the /group-requests page and this page look
// like one "Requests" section from the sidebar. Kept as a plain flat
// component so it renders identically on both pages.
export function RequestsTabs({ active }) {
  return (
    <div className="flex gap-1 border-b border-[#262626] mb-4 -mt-2" data-testid="requests-tabs">
      {[
        { key: "match", to: "/requests", label: "Matchmaking" },
        { key: "sessions", to: "/group-requests", label: "Session requests" },
      ].map((t) => (
        <Link
          key={t.key}
          to={t.to}
          data-testid={`requests-tab-${t.key}`}
          className="px-4 py-2 text-xs uppercase tracking-wider transition-colors"
          style={active === t.key
            ? { color: "#CCFF00", borderBottom: "2px solid #CCFF00", marginBottom: -1 }
            : { color: "#A3A3A3", borderBottom: "2px solid transparent", marginBottom: -1 }}
        >
          {t.label}
        </Link>
      ))}
    </div>
  );
}

const SKILL_LEVELS = ["beginner", "improver", "intermediate", "advanced", "elite"];
// Map DUPR self-rating → canonical skill-level label so we can pre-fill the form.
const DUPR_TO_SKILL_LEVEL = {
  "2.5": "beginner",
  "3.0": "improver",
  "3.5": "intermediate",
  "4.0": "advanced",
  "4.5": "elite",
};
const TIME_WINDOWS = [
  { v: "morning", l: "Morning (7-12)" },
  { v: "afternoon", l: "Afternoon (12-17)" },
  { v: "evening", l: "Evening (17-22)" },
];
const FORMAT_OPTIONS = [
  { v: "any", l: "Not fussy" },
  { v: "group", l: "Group" },
  { v: "one_on_one", l: "1-to-1" },
];
const MIN_DATES = 3;

export default function Requests() {
  const { user } = useAuth();
  const isCoach = user?.role === "coach" || user?.role === "admin";
  const [items, setItems] = useState([]);
  const reqCtl = useListControls(items, { name: (r) => r.student_name || r.title || "", fields: (r) => [r.student_name, r.title, r.notes, r.sport], initialSort: "default" });
  const [matches, setMatches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [scheduleCluster, setScheduleCluster] = useState(null);
  // Coach-only filter strip — "all" (default), "group", "one_on_one"
  const [matchFilter, setMatchFilter] = useState("all");
  // Skill-level filter — "all" (default), "beginner", "intermediate", "advanced"
  const [skillFilter, setSkillFilter] = useState("all");

  const load = async () => {
    const r = await api.get("/requests");
    setItems(r.data);
    if (isCoach) {
      const m = await api.get("/requests/matches");
      setMatches(m.data);
    }
  };

  useEffect(() => { load();   }, []);

  const remove = async (id) => {
    try { await api.delete(`/requests/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <RequestsTabs active="match" />
      <div className="flex items-end justify-between flex-wrap gap-4 fade-up">
        <div>
          <div className="label-mono mb-2">TAILORED REQUESTS</div>
          <h1 className="h-display text-5xl tracking-tighter">{isCoach ? "Match & schedule." : "Request a tailored session."}</h1>
          <div className="text-sm text-[#A3A3A3] mt-1 max-w-xl">
            {isCoach
              ? "Off-schedule sessions formed from clusters of player requests with matching skill level, focus areas and availability."
              : "Pick the skills you want coached, your skill level, and at least 3 dates within the next 30 days. We'll group you with other players when interest aligns."}
          </div>
        </div>
        {!isCoach && (
          <button onClick={() => setShowForm(true)} className="btn-volt px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="new-request-btn">
            <Plus className="w-4 h-4" /> New request
          </button>
        )}
      </div>

      {/* COACH: clusters */}
      {isCoach && (
        <>
          <div className="card-surface p-6 fade-up d1">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#CCFF00]" />
                <div className="label-mono text-[#CCFF00]">SUGGESTED CLUSTERS · {matches.length}</div>
              </div>
              {/* Format filter strip — All / Group / 1-to-1. Counts update live. */}
              <div className="flex border border-[#262626]" data-testid="cluster-format-filter">
                {[
                  { v: "all", l: "All", count: matches.length },
                  { v: "group", l: "Group", count: matches.filter((c) => c.format === "group").length },
                  { v: "one_on_one", l: "1-to-1", count: matches.filter((c) => c.format === "one_on_one").length },
                ].map((opt) => {
                  const on = matchFilter === opt.v;
                  return (
                    <button key={opt.v} onClick={() => setMatchFilter(opt.v)}
                      data-testid={`filter-${opt.v}`}
                      className="px-3 py-1.5 text-[10px] uppercase tracking-wider border-r border-[#262626] last:border-r-0 transition-colors"
                      style={on
                        ? { background: "#CCFF00", color: "#0A0A0A", fontWeight: 700 }
                        : { background: "transparent", color: "#A3A3A3" }}>
                      {opt.l} <span className="opacity-70 ml-1">{opt.count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Skill-level filter — independent of format. Coaches running a session prefer
                everyone aiming for the same level so sessions don't fragment. */}
            <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="cluster-skill-filter-wrapper">
              <span className="label-mono text-[#A3A3A3]">Skill level</span>
              <div className="flex border border-[#262626] flex-wrap" data-testid="cluster-skill-filter">
                {[
                  { v: "all", l: "All" },
                  { v: "beginner", l: "Beginner" },
                  { v: "improver", l: "Improver" },
                  { v: "intermediate", l: "Intermediate" },
                  { v: "advanced", l: "Advanced" },
                  { v: "elite", l: "Elite" },
                ].map((opt) => {
                  const on = skillFilter === opt.v;
                  const count = opt.v === "all" ? matches.length : matches.filter((c) => c.skill_level === opt.v).length;
                  return (
                    <button key={opt.v} onClick={() => setSkillFilter(opt.v)}
                      data-testid={`skill-filter-${opt.v}`}
                      className="px-3 py-1.5 text-[10px] uppercase tracking-wider border-r border-[#262626] last:border-r-0 transition-colors"
                      style={on
                        ? { background: "#007AFF", color: "#0A0A0A", fontWeight: 700 }
                        : { background: "transparent", color: "#A3A3A3" }}>
                      {opt.l} <span className="opacity-70 ml-1">{count}</span>
                    </button>
                  );
                })}
              </div>
              {(matchFilter !== "all" || skillFilter !== "all") && (
                <button onClick={() => { setMatchFilter("all"); setSkillFilter("all"); }}
                  data-testid="clear-filters"
                  className="text-[10px] uppercase tracking-wider text-[#FF4500] hover:text-white transition-colors ml-auto">
                  Clear filters ×
                </button>
              )}
            </div>
            {(() => {
              const filtered = matches.filter((c) => {
                if (matchFilter !== "all" && c.format !== matchFilter) return false;
                if (skillFilter !== "all" && c.skill_level !== skillFilter) return false;
                return true;
              });
              if (!filtered.length) {
                const formatLabel = matchFilter === "all" ? "" : matchFilter === "group" ? "group" : "1-to-1";
                const skillLabel = skillFilter === "all" ? "" : skillFilter;
                const labelParts = [skillLabel, formatLabel].filter(Boolean).join(" · ");
                return (
                  <div className="text-sm text-[#A3A3A3]" data-testid="no-clusters">
                    {matches.length === 0
                      ? "No matches yet. Players with shared skill level, focus and overlapping availability will cluster here automatically."
                      : `No ${labelParts || "matching"} candidates right now.`}
                  </div>
                );
              }
              return (
            <div className="space-y-3">
              {filtered.map((c, i) => {
                const m = SPORT_META[c.sport];
                const is11 = c.format === "one_on_one";
                return (
                  <div key={i} className="border border-[#262626] p-4" data-testid={`cluster-${i}`}
                       style={is11 ? { borderLeftColor: "#CCFF00", borderLeftWidth: 3 } : {}}>
                    <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="label-mono" style={{ color: is11 ? "#CCFF00" : "#FF4500" }}>
                            {is11 ? "1-TO-1" : "GROUP"}
                          </span>
                          <span className="label-mono" style={{ color: m.color }}>{c.sport.toUpperCase()}</span>
                          <span className="label-mono" style={{ color: "#007AFF" }}>{c.skill_level}</span>
                          <span className="label-mono">{c.time_window}</span>
                        </div>
                        <div className="h-display text-xl tracking-tight">
                          {is11
                            ? `${c.members[0].student_name} · ${new Date(c.candidate_date).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}`
                            : `${c.member_count} players · ${new Date(c.candidate_date).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}`}
                        </div>
                        <div className="text-xs text-[#A3A3A3] mt-1">
                          {is11 ? "Focus" : "Shared focus"}: {(c.shared_focus.length ? c.shared_focus : c.union_focus).join(", ") || "—"}
                        </div>
                      </div>
                      <button onClick={() => setScheduleCluster(c)} className="btn-volt px-4 py-2 text-xs inline-flex items-center gap-2" data-testid={`schedule-${i}`}>
                        <CalendarDays className="w-3.5 h-3.5" /> Schedule {is11 ? "1:1" : "session"}
                      </button>
                    </div>
                    {!is11 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {c.members.map((m2) => (
                          <span key={m2.id} className="text-[10px] uppercase tracking-wider border border-[#262626] px-2 py-0.5">
                            {m2.student_name}
                            {m2.format_preference === "any" && <span className="ml-1 text-[#A3A3A3]">(flex)</span>}
                          </span>
                        ))}
                      </div>
                    )}
                    {c.alt_dates?.length > 0 && (
                      <div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider mt-2">
                        Alt dates: {c.alt_dates.join(", ")}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
              );
            })()}
          </div>
        </>
      )}

      {/* List of requests */}
      <div className="space-y-3 fade-up d2">
        <div className="label-mono">{isCoach ? "ALL OPEN REQUESTS" : "MY REQUESTS"} ({items.length})</div>
        {!items.length && <div className="text-sm text-[#A3A3A3]">No requests yet.</div>}
        {items.length > 0 && <ListControlsBar ctl={reqCtl} modes={["default", "az", "za"]} placeholder="Search requests…" testid="requests" />}
        {reqCtl.list.map((r) => {
          const m = SPORT_META[r.sport];
          return (
            <div key={r.id} className="card-surface p-4 flex items-start gap-4 flex-wrap" data-testid={`request-${r.id}`}>
              <div className="w-1 self-stretch min-h-[40px]" style={{ background: m.color }} />
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="label-mono" style={{ color: m.color }}>{r.sport.toUpperCase()}</span>
                  <span className="label-mono">{r.skill_level}</span>
                  <span className="label-mono">{r.time_window}</span>
                  {r.format_preference && r.format_preference !== "any" && (
                    <span className="label-mono" style={{ color: "#CCFF00" }}>
                      · prefers {r.format_preference === "one_on_one" ? "1-to-1" : r.format_preference}
                    </span>
                  )}
                  <span className="label-mono" style={{ color: r.status === "scheduled" ? "#CCFF00" : "#A3A3A3" }}>· {r.status}</span>
                </div>
                <div className="text-sm font-semibold">{isCoach ? r.student_name : "Your request"}</div>
                <div className="text-xs text-[#A3A3A3] mt-0.5">Focus: {r.skill_focus.join(", ") || "—"}</div>
                <div className="text-xs text-[#A3A3A3] mt-0.5">{r.availability_dates.length} dates available</div>
                {r.notes && <div className="text-xs mt-1 text-[#E5E5E5]">&ldquo;{r.notes}&rdquo;</div>}
              </div>
              <button onClick={() => remove(r.id)} className="text-[#FF4500] hover:opacity-70" data-testid={`del-req-${r.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          );
        })}
      </div>

      {showForm && <RequestForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {scheduleCluster && <ScheduleClusterModal cluster={scheduleCluster} onClose={() => setScheduleCluster(null)} onSaved={() => { setScheduleCluster(null); load(); }} />}
    </div>
  );
}

function RequestForm({ onClose, onSaved }) {
  const { user } = useAuth();
  const { enabledSports } = useSport();
  const squashEnabled = (enabledSports || []).includes("squash");
  // Default to the first sport that's actually enabled — usually pickleball.
  const [sport, setSport] = useState(squashEnabled ? "squash" : "pickleball");
  // Pre-fill the skill level from the player's DUPR self-rating so they don't have
  // to pick it twice. They can still override if they want to train against a
  // higher (or lower) cohort for a specific session.
  const inferredLevel = (() => {
    const d = user?.self_rating?.level || user?.self_rating?.dupr;
    if (!d) return "intermediate";
    const key = typeof d === "number" ? d.toFixed(1) : String(d);
    return DUPR_TO_SKILL_LEVEL[key] || "intermediate";
  })();
  const [skillLevel, setSkillLevel] = useState(inferredLevel);
  const [timeWindow, setTimeWindow] = useState("evening");
  const [formatPreference, setFormatPreference] = useState("any");
  const [skillFocus, setSkillFocus] = useState([]);
  const [selectedDates, setSelectedDates] = useState(new Set());
  const [notes, setNotes] = useState("");
  const [skills, setSkills] = useState([]);
  const [saving, setSaving] = useState(false);
  const [coaches, setCoaches] = useState([]);
  const [coachId, setCoachId] = useState("");
  const [dayStatus, setDayStatus] = useState({});
  const [openWindows, setOpenWindows] = useState({});
  const [peekDay, setPeekDay] = useState(null);
  const [coachWindows, setCoachWindows] = useState({});
  const selectedCoach = coaches.find((c) => c.id === coachId);

  useEffect(() => {
    api.get("/public/coaches").then((r) => setCoaches(r.data || [])).catch(() => setCoaches([]));
  }, []);

  // If admin disables squash mid-session and the user had it selected, snap back to pickleball.
  useEffect(() => {
    if (!squashEnabled && sport === "squash") setSport("pickleball");
  }, [squashEnabled, sport]);

  useEffect(() => {
    api.get("/config").then((r) => setSkills(r.data.sport_skills?.[sport] || []));
  }, [sport]);

  const today = new Date();
  // build a 6-week (42-day) grid starting today
  const gridDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
      days.push(d);
    }
    return days;
     
  }, []);

  const gridStart = ymdLocal(gridDays[0]);
  const gridEnd = ymdLocal(gridDays[gridDays.length - 1]);
  useEffect(() => {
    setPeekDay(null);
    if (coachId) {
      api.get(`/public/coaches/${coachId}/day-status?start=${gridStart}&end=${gridEnd}&sport=${sport}`)
        .then((r) => {
          const st = Object.fromEntries((r.data.days || []).map((x) => [x.date, x.status]));
          setDayStatus(st);
          setCoachWindows(r.data.time_windows || {});
          setSelectedDates((prev) => {
            const keep = new Set([...prev].filter((iso) => st[iso] !== "blocked"));
            if (keep.size < prev.size) toast.warning("Removed dates this coach is unavailable on.");
            return keep;
          });
        })
        .catch(() => setDayStatus({}));
    } else {
      setDayStatus({});
      setCoachWindows({});
      api.get(`/public/coach-open-windows?start=${gridStart}&end=${gridEnd}&sport=${sport}`)
        .then((r) => setOpenWindows(r.data.days || {}))
        .catch(() => setOpenWindows({}));
    }
  }, [coachId, gridStart, gridEnd, sport]);

  const dayAvail = (iso) => (coachId ? (dayStatus[iso] || "negotiable") : (openWindows[iso]?.length ? "available" : "negotiable"));

  const coachBands = useMemo(() => {
    const picked = [...selectedDates].filter((iso) => coachWindows[iso]);
    const src = picked.length ? picked.map((iso) => coachWindows[iso]) : Object.values(coachWindows);
    return TIME_WINDOWS.map((t) => t.v).filter((v) => src.some((b) => b.includes(v)));
  }, [coachWindows, selectedDates]);
  useEffect(() => {
    const all = TIME_WINDOWS.map((t) => t.v).filter((v) => Object.values(coachWindows).some((b) => b.includes(v)));
    if (all.length) setTimeWindow((tw) => (all.includes(tw) ? tw : all[0]));
  }, [coachWindows]);

  const toggleDate = (d) => {
    if (d > new Date(today.getFullYear(), today.getMonth(), today.getDate() + 30)) return; // 30-day window
    const iso = ymdLocal(d);
    if (!coachId && openWindows[iso]?.length) setPeekDay(iso);
    if (coachId && dayStatus[iso] === "blocked" && !selectedDates.has(iso)) {
      toast.error("This coach is not available on the selected date. Please choose a different date or coach.");
      return;
    }
    setSelectedDates((prev) => {
      const next = new Set(prev);
      if (next.has(iso)) next.delete(iso); else next.add(iso);
      return next;
    });
  };

  const toggleSkill = (s) => {
    setSkillFocus((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  };

  const count = selectedDates.size;
  const hasUnconfirmed = !!coachId && [...selectedDates].some((iso) => dayStatus[iso] !== "available");
  const minDates = coachId && !hasUnconfirmed ? 1 : MIN_DATES;
  const valid = count >= minDates;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { toast.error(`Select at least ${minDates} date${minDates === 1 ? "" : "s"}`); return; }
    setSaving(true);
    try {
      await api.post("/requests", {
        sport, skill_level: skillLevel, skill_focus: skillFocus,
        availability_dates: Array.from(selectedDates), time_window: timeWindow,
        format_preference: formatPreference, notes,
        preferred_coach_id: coachId || null,
      });
      toast.success("Request submitted");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="card-surface w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto" data-testid="request-form">
        <div className="flex items-center justify-between p-6 border-b border-[#262626] sticky top-0 bg-[#141414] z-10">
          <div>
            <div className="label-mono mb-1">NEW REQUEST</div>
            <h2 className="h-display text-2xl">Tailored session request</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="req-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          <Picker label="Sport" value={sport} onChange={setSport} options={[
            { v: "squash", l: "Squash", c: "#CCFF00", disabled: !squashEnabled, disabledReason: "Squash isn't enabled on your account yet — ask your coach to switch it on." },
            { v: "pickleball", l: "Pickleball", c: "#FF4500" },
          ]} testid="req-sport" />

          <Picker label="Your level" value={skillLevel} onChange={setSkillLevel}
            hint={user?.self_rating?.level ? `Pre-filled from your DUPR ${user.self_rating.level} — change if needed` : undefined}
            options={SKILL_LEVELS.map((l) => ({ v: l, l: l.charAt(0).toUpperCase() + l.slice(1), c: "#007AFF" }))} testid="req-level" />

          <Picker label="Format preference" value={formatPreference} onChange={setFormatPreference}
            options={FORMAT_OPTIONS.map((f) => ({ v: f.v, l: f.l, c: "#CCFF00" }))} testid="req-format" />

          <Picker label="Preferred time window" value={timeWindow} onChange={setTimeWindow}
            options={TIME_WINDOWS.map((t) => ({ v: t.v, l: t.l, c: "#FF4500", hl: coachBands.includes(t.v) }))} testid="req-window" />
          {coachId && coachBands.length > 0 && (
            <div className="text-xs text-[#A3A3A3] -mt-3" data-testid="req-window-notice">
              <span className="text-[#22C55E]">Green-outlined</span> windows are when {selectedCoach?.name || "your coach"} is available. Other times may not be available and cannot be guaranteed.
            </div>
          )}

          <div>
            <div className="label-mono mb-2">Skill focus (pick any)</div>
            <div className="flex flex-wrap gap-2">
              {skills.map((s) => {
                const on = skillFocus.includes(s);
                return (
                  <button key={s} type="button" onClick={() => toggleSkill(s)} data-testid={`reqfocus-${s.replace(/\s+/g,"")}`}
                    className="px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors"
                    style={on ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00", fontWeight: 700 } : { borderColor: "#262626", color: "#A3A3A3" }}>
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="label-mono mb-2">Preferred coach (optional)</div>
            <select value={coachId} onChange={(e) => setCoachId(e.target.value)} data-testid="req-coach"
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]">
              <option value="">No preference — any coach</option>
              {coaches.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {selectedCoach?.student_arranges_venue && (
              <div className="mt-2 border-l-2 border-[#FF9F1C] bg-[#1A1408] px-3 py-2 text-sm text-white" data-testid="req-venue-notice">
                Note: your coach requires you to organise and pay for the venue directly.
              </div>
            )}
          </div>

          {/* Date picker */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-mono">Availability — pick ≥{minDates} date{minDates === 1 ? "" : "s"} in next 30 days</div>
              <div className={`label-mono ${valid ? "text-[#CCFF00]" : "text-[#FF4500]"}`} data-testid="date-count">{count}/{minDates}</div>
            </div>
            <div className="grid grid-cols-7 gap-1">
              {["M","T","W","T","F","S","S"].map((d, i) => (
                <div key={i} className="label-mono text-center text-[0.6rem] py-1">{d}</div>
              ))}
              {Array.from({ length: (gridDays[0].getDay() + 6) % 7 }).map((_, i) => <div key={`pad-${i}`} />)}
              {gridDays.map((d) => {
                const iso = ymdLocal(d);
                const inWindow = d <= new Date(today.getFullYear(), today.getMonth(), today.getDate() + 30);
                const isPast = d < new Date(today.getFullYear(), today.getMonth(), today.getDate());
                const selected = selectedDates.has(iso);
                const disabled = !inWindow || isPast;
                const av = disabled ? null : dayAvail(iso);
                return (
                  <button key={iso} type="button" disabled={disabled} onClick={() => toggleDate(d)} data-testid={`date-${iso}`} data-avail={av || "none"}
                    className="aspect-square text-xs flex flex-col items-center justify-center border transition-colors"
                    style={selected
                      ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00", fontWeight: 700 }
                      : disabled
                        ? { borderColor: "#1a1a1a", color: "#3a3a3a", cursor: "not-allowed" }
                        : av === "available"
                          ? { borderColor: "#22C55E", background: "rgba(34,197,94,0.18)", color: "#E5FFE9" }
                          : av === "blocked"
                            ? { borderColor: "#FF4D4D", background: "rgba(255,77,77,0.15)", color: "#FF9A9A", textDecoration: "line-through" }
                            : { borderColor: "#262626", color: "#E5E5E5" }}>
                    <div className="text-sm">{d.getDate()}</div>
                    <div className="text-[0.55rem] opacity-60">{d.toLocaleDateString(undefined,{month:"short"})}</div>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-3 mt-2 text-[0.65rem] text-[#B0B0B0]" data-testid="req-avail-legend">
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 border border-[#22C55E] bg-[rgba(34,197,94,0.18)]" /> {coachId ? "Coach available" : "A coach is available"}</span>
              {coachId && <span className="inline-flex items-center gap-1"><span className="w-3 h-3 border border-[#FF4D4D] bg-[rgba(255,77,77,0.15)]" /> Unavailable</span>}
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 border border-[#262626]" /> Negotiable</span>
            </div>
            {hasUnconfirmed && (
              <div className="mt-3 border-l-2 border-[#FF9F1C] bg-[#1A1408] px-3 py-2 text-xs text-white" data-testid="req-unconfirmed-notice">
                This date isn't confirmed — please select at least 3 options so your coach can find the best match.
              </div>
            )}
            {!coachId && peekDay && openWindows[peekDay]?.length > 0 && (
              <div className="mt-3 border border-[#22C55E]/50 bg-[#0F1A12] p-3" data-testid="req-day-coaches">
                <div className="label-mono text-[#22C55E] mb-2">Available on {new Date(`${peekDay}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}</div>
                <ul className="space-y-1 text-sm">
                  {openWindows[peekDay].map((w, i) => (
                    <li key={i} className="flex justify-between gap-3" data-testid={`req-day-coach-${i}`}>
                      <span className="text-white">{w.coach_name}</span>
                      <span className="text-[#B0B0B0] font-mono text-xs">
                        {new Date(w.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}–{new Date(w.end).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="text-[0.65rem] text-[#A3A3A3] mt-2 label-mono">
              Tailored sessions are off-schedule events — your coach matches you with players sharing similar skill, focus and dates.
            </div>
          </div>

          <div>
            <div className="label-mono mb-2">Notes (optional)</div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="req-notes"
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]" />
          </div>

          <button type="submit" disabled={!valid || saving} className="btn-volt w-full py-3 text-sm disabled:opacity-30" data-testid="req-submit">
            {saving ? "Submitting…" : valid ? "Submit request" : `Pick ${minDates - count} more date${minDates - count === 1 ? "" : "s"}`}
          </button>
        </div>
      </form>
    </div>
  );
}

function ScheduleClusterModal({ cluster, onClose, onSaved }) {
  const is11 = cluster.format === "one_on_one";
  const [venue, setVenue] = useState("");
  const [time, setTime] = useState("18:00");
  const [duration, setDuration] = useState(60);
  // 1:1 sessions default to a higher per-player price (matches the £30-60 tier from the brief).
  const [price, setPrice] = useState(is11 ? 40 : 15);
  // 1:1 sessions cap capacity at 1 — the form below hides the spaces field entirely for clarity.
  const [maxSpaces, setMaxSpaces] = useState(is11 ? 1 : Math.max(cluster.member_count + 2, 6));
  const [title, setTitle] = useState(
    is11
      ? `1:1 · ${cluster.members[0]?.student_name || "Player"}`
      : `Tailored ${cluster.sport} · ${cluster.skill_level}`
  );
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!venue.trim()) { toast.error("Venue required"); return; }
    setSaving(true);
    try {
      await api.post("/requests/cluster-to-offering", {
        format: cluster.format || "group",
        sport: cluster.sport,
        skill_level: cluster.skill_level,
        candidate_date: cluster.candidate_date,
        time_window: cluster.time_window,
        member_ids: cluster.members.map((m) => m.id),
        venue, start_time: time, duration_min: duration, max_spaces: maxSpaces,
        price_per_player: price, title,
        skill_focus: cluster.shared_focus.length ? cluster.shared_focus : cluster.union_focus,
      });
      toast.success(is11 ? "1:1 session scheduled" : "Session scheduled & published");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="card-surface w-full max-w-lg" data-testid="schedule-modal">
        <div className="flex items-center justify-between p-6 border-b border-[#262626]">
          <div>
            <div className="label-mono mb-1">{is11 ? "SCHEDULE 1:1" : "SCHEDULE CLUSTER"}</div>
            <h2 className="h-display text-2xl">
              {is11
                ? `${cluster.members[0]?.student_name} · ${new Date(cluster.candidate_date).toLocaleDateString(undefined, { day: "numeric", month: "short" })}`
                : `${cluster.member_count} players · ${new Date(cluster.candidate_date).toLocaleDateString(undefined, { day: "numeric", month: "short" })}`}
            </h2>
          </div>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Title" value={title} onChange={setTitle} testid="sched-title" />
          <Field label="Venue" value={venue} onChange={setVenue} required testid="sched-venue" />
          <div className={`grid ${is11 ? "grid-cols-2" : "grid-cols-3"} gap-3`}>
            <Field label="Time" type="time" value={time} onChange={setTime} testid="sched-time" />
            <Field label="Duration" type="number" value={duration} onChange={setDuration} testid="sched-duration" />
            {!is11 && <Field label="Spaces" type="number" value={maxSpaces} onChange={setMaxSpaces} testid="sched-spaces" />}
          </div>
          <Field label={is11 ? "Price (£)" : "Price per player (£)"} type="number" value={price} onChange={setPrice} testid="sched-price" />
          <button type="submit" disabled={saving} className="btn-volt w-full py-3 text-sm" data-testid="sched-submit">
            {saving ? "Scheduling…" : is11 ? "Schedule 1:1" : "Schedule & publish"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Picker({ label, value, onChange, options, testid, hint }) {
  // Auto-pick grid width based on option count so 5-tier skill pickers don't crush.
  const cols = options.length >= 5 ? "grid-cols-3 sm:grid-cols-5" : options.length === 4 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3";
  return (
    <div>
      <div className="label-mono mb-2 flex items-center justify-between gap-2">
        <span>{label}</span>
        {hint && <span className="text-[0.6rem] text-[#A3A3A3] normal-case tracking-normal">{hint}</span>}
      </div>
      <div className={`grid ${cols} gap-2`} data-testid={testid}>
        {options.map((o) => {
          const on = value === o.v;
          const disabled = !!o.disabled;
          return (
            <button key={o.v} type="button"
              disabled={disabled}
              title={disabled ? (o.disabledReason || "Unavailable on your account") : undefined}
              onClick={() => !disabled && onChange(o.v)}
              data-testid={`${testid}-opt-${o.v}`}
              className="py-2.5 text-xs uppercase tracking-wider border transition-colors relative"
              style={disabled
                ? { borderColor: "#1a1a1a", color: "#3a3a3a", background: "#0d0d0d", cursor: "not-allowed", textDecoration: "line-through" }
                : on
                  ? { background: o.c, color: "#0A0A0A", borderColor: o.c, fontWeight: 700, boxShadow: o.hl ? "0 0 0 2px #22C55E" : undefined }
                  : o.hl
                    ? { borderColor: "#22C55E", color: "#E5FFE9", background: "rgba(34,197,94,0.12)" }
                    : { borderColor: "#262626", color: "#A3A3A3" }}
              data-coach-available={o.hl ? "true" : undefined}>
              {o.l}{disabled && <span className="block text-[0.55rem] tracking-normal normal-case opacity-70 mt-0.5">Not enabled</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <div className="label-mono mb-2">{label}{required && <span className="text-[#FF4500]"> *</span>}</div>
      <input type={type} value={value ?? ""} required={required} onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)} data-testid={testid}
        className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]" />
    </div>
  );
}
